import type { Contract, ContractTemplate } from '@shared/schema';

export interface ContractAcceptResult extends Contract {
  stripeSessionUrl?: string;
}

/**
 * Fetch the active contract template by type and language.
 * Returns null if no template found or on error.
 */
export async function fetchContractTemplate(
  type: string,
  language = 'fr',
): Promise<ContractTemplate | null> {
  try {
    const res = await fetch(
      `/api/contract-templates?type=${encodeURIComponent(type)}&language=${encodeURIComponent(language)}`,
    );
    if (!res.ok) return null;
    const json: ContractTemplate | null = await res.json();
    return json;
  } catch {
    return null;
  }
}

/**
 * Create a contract instance from a template, then immediately accept it.
 * Returns the signed contract record.
 */
export async function acceptContractTemplate(
  templateId: string,
  options?: { creatorId?: string; clientId?: string; projectId?: string },
): Promise<Contract> {
  // Step 1: Create draft contract instance
  const createRes = await fetch('/api/contracts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ contractTemplateId: templateId, ...options }),
  });
  if (!createRes.ok) {
    const text = await createRes.text();
    throw new Error(`Failed to create contract: ${text}`);
  }
  const contract: Contract = await createRes.json();

  // Step 2: Accept the contract
  const acceptRes = await fetch(`/api/contracts/${contract.id}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!acceptRes.ok) {
    const text = await acceptRes.text();
    throw new Error(`Failed to accept contract: ${text}`);
  }
  return acceptRes.json();
}

/**
 * Accept an existing draft contract by ID and return the signed contract.
 * If the backend returns a stripeSessionUrl, it will be included in the result.
 */
export async function acceptContractForCheckout(
  contractId: string,
): Promise<ContractAcceptResult> {
  const res = await fetch(`/api/contracts/${encodeURIComponent(contractId)}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Impossible d'accepter le contrat : ${text}`);
  }
  return res.json();
}
